import { useCallback, useRef } from "react";

/**
 * For an overlay whose action HANDS OFF to a modal instead of just dismissing:
 * the automations popover opening the automations dialog, a menu item opening a
 * confirmation.
 *
 * Base UI restores focus to the overlay's trigger when the overlay closes. The
 * modal opening in the same tick marks everything outside itself `aria-hidden`
 * — the trigger included — so that restored focus lands inside a hidden
 * subtree. Chrome refuses to hide a subtree containing focus, so it drops the
 * `aria-hidden` entirely and the whole background stays exposed to assistive
 * technology, which is the opposite of what the modal asked for.
 *
 * A DISMISSAL must still restore focus — that is the keyboard user's way back
 * to where they were. Base UI's `finalFocus` callback receives the interaction
 * type (mouse/touch/pen/keyboard), which does not say whether an item was
 * activated or the overlay was waved away, so the hand-off marks itself:
 *
 *   const { handOff, finalFocus, onOpenChange } = useOverlayHandoff();
 *   <Menu onOpenChange={onOpenChange}>
 *     <MenuItem onClick={() => handOff(() => setConfirmingDelete(true))} />
 *     <MenuPopup finalFocus={finalFocus} />
 *
 * The modal moves focus into itself on open; suppressing the restore only stops
 * the two from racing.
 */
export function useOverlayHandoff() {
  const handingOff = useRef(false);

  // Wraps the action that opens the modal. The open waits a frame so the
  // overlay is gone before the modal hides the background: a menu closes on its
  // own schedule rather than with our state, so opening in the same tick means
  // the modal marks the background `aria-hidden` while the menu item still
  // holds focus — the same thing Chrome refuses, one layer up.
  const handOff = useCallback((open: () => void) => {
    handingOff.current = true;
    requestAnimationFrame(open);
  }, []);

  // `false` tells Base UI to leave focus alone, `true` to restore as usual. The
  // flag is consumed on read so one hand-off cannot silence a later dismissal.
  const finalFocus = useCallback(() => {
    const handingOffNow = handingOff.current;
    handingOff.current = false;
    return !handingOffNow;
  }, []);

  // Reopening clears the flag, in case a close path never asked for focus.
  const onOpenChange = useCallback((next: boolean) => {
    if (next) handingOff.current = false;
  }, []);

  return { handOff, finalFocus, onOpenChange };
}
